/**
 * YouTube IFrame API wrapper for a single track.
 *
 * Creates a hidden iframe via YT.Player, loads the YouTube player,
 * and exposes play/pause/volume/seek control via the YT API.
 *
 * Auto-skips to 60s on first play to jump past intros.
 * Handles browser autoplay policy: user gesture unlocks all players.
 */

const SKIP_MS = 60_000;
const YT_API_SRC = 'https://www.youtube.com/iframe_api';

type YTPlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  setVolume: (vol: number) => void;
  getVolume: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  destroy: () => void;
  addEventListener: (event: string, handler: (data?: any) => void) => void;
};

declare global {
  interface Window {
    YT: {
      Player: new (el: string | HTMLElement, opts: YTPlayerOptions) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
      loaded: number;
      ready: (cb: () => void) => void;
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YTPlayerOptions {
  videoId?: string;
  width?: number | string;
  height?: number | string;
  playerVars?: Record<string, string | number>;
  events?: Record<string, (e?: any) => void>;
}

export class YouTubePlayer {
  trackId: string;
  videoId: string;
  widget: YTPlayer | null = null;
  loaded = false;
  playing = false;
  volume = 100;
  unlocked = false;
  private initialSeeked = false;
  private loading = false;
  private containerId: string;

  constructor(trackId: string, embedUrl: string) {
    this.trackId = trackId;
    this.containerId = `yt-${trackId}`;
    const params = new URLSearchParams(embedUrl.split('?')[1] || '');
    this.videoId = params.get('v') || embedUrl.split('/').pop() || '';
  }

  async load(): Promise<void> {
    if (this.loaded || this.loading) return;
    this.loading = true;

    await this.ensureYTAPI();

    // Create container — YT.Player will create its own iframe inside
    const container = document.createElement('div');
    container.id = this.containerId;
    container.style.cssText =
      'position:fixed;width:2px;height:2px;border:0;opacity:1;pointer-events:none;bottom:0;right:0;overflow:hidden;';
    document.body.appendChild(container);

    new window.YT.Player(container, {
      videoId: this.videoId,
      width: 2,
      height: 2,
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        enablejsapi: 1,
        fs: 0,
        modestbranding: 1,
        origin: window.location.origin,
        playsinline: 1,
        rel: 0,
      },
      events: {
        onReady: (event: any) => {
          const player = event?.target;
          if (player && typeof player.playVideo === 'function') {
            this.widget = player;
          }
          this.loaded = true;
          this.loading = false;
          console.log(`[YT] Ready: ${this.trackId}`);
          if (this.widget) {
            this.widget.setVolume(this.volume);
          }
        },
        onStateChange: (event: any) => {
          const state = event?.data;
          if (state === window.YT?.PlayerState?.PLAYING) {
            this.playing = true;
            if (!this.initialSeeked && this.widget) {
              const dur = this.widget.getDuration() || 0;
              const skipTo = Math.min(SKIP_MS / 1000, dur * 0.3);
              if (skipTo > 2) {
                this.widget.seekTo(skipTo, true);
                this.initialSeeked = true;
              }
            }
          } else if (state === window.YT?.PlayerState?.PAUSED) {
            this.playing = false;
          }
        },
        onError: (event: any) => {
          console.warn(`[YT] Error on ${this.trackId}:`, event?.data);
          this.loading = false;
        },
      },
    });
  }

  private ensureYTAPI(): Promise<void> {
    return new Promise((resolve) => {
      if (window.YT?.Player) {
        resolve();
        return;
      }
      const tag = document.createElement('script');
      tag.src = YT_API_SRC;
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag!.parentNode!.insertBefore(tag, firstScriptTag);
      window.onYouTubeIframeAPIReady = () => {
        resolve();
      };
      setTimeout(() => resolve(), 10000);
    });
  }

  async unlock(): Promise<void> {
    if (!this.widget || this.unlocked) return;
    try {
      this.widget.playVideo();
      await new Promise((r) => setTimeout(r, 300));
      this.widget.pauseVideo();
      this.unlocked = true;
      console.log(`[YT] Unlocked: ${this.trackId}`);
    } catch (e) {
      console.warn(`[YT] Unlock failed for ${this.trackId}:`, e);
    }
  }

  play(): void {
    if (!this.widget) return;
    this.widget.playVideo();
    this.playing = true;
  }

  pause(): void {
    if (!this.widget) return;
    this.widget.pauseVideo();
    this.playing = false;
  }

  setVolume(vol: number): void {
    if (!this.widget) return;
    this.volume = Math.max(0, Math.min(100, Math.round(vol)));
    this.widget.setVolume(this.volume);
  }

  destroy(): void {
    this.pause();
    if (this.widget) {
      this.widget.destroy();
    }
    this.widget = null;
    this.loaded = false;
    this.loading = false;
    this.initialSeeked = false;
    const container = document.getElementById(this.containerId);
    if (container) container.remove();
  }
}
